import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import BudgetEstimateField from '@/components/BudgetEstimateField';
import VenueAutocomplete from '@/components/VenueAutocomplete';
import WeddingHoursField from '@/components/WeddingHoursField';

/**
 * The fields the new-wedding and edit-wedding dialogs both collect.
 *
 * The two dialogs stay separate components because one thing genuinely differs
 * between them: the new dialog previews the Gmail label it is about to commit
 * to, the edit dialog explains why that label is frozen once mail has gone out.
 * Everything above that panel is the same form twice — four name inputs, a
 * date, a pair of times, a venue, two estimates — and typing it out twice is
 * how the two dialogs drift apart a field at a time.
 *
 * Every value here is a string, the numbers and the times included, because a
 * string is what an `<input>` holds. `''` means "not set" throughout; turning
 * that into the `null` the API wants belongs to the caller, at the moment it
 * builds the request body, where the API's rules are already in view.
 */

export interface WeddingFormState {
  partner1FirstName: string;
  partner1LastName: string;
  partner2FirstName: string;
  partner2LastName: string;
  /** `YYYY-MM-DD`, the shape a date input reads and writes. */
  weddingDate: string;
  /** `HH:MM` — never the `HH:MM:SS` Postgres returns, which is not a time any
   *  input, native or not, agrees to read back. */
  startTime: string;
  endTime: string;
  venueName: string;
  /**
   * Filled in by an accepted Places suggestion, and cleared by any manual edit
   * to the venue text — `VenueAutocomplete` sets all three together or clears
   * these two together, so a stored place id never disagrees with the name
   * beside it. Both stay in form state so a save that only changed the guest
   * count round-trips them instead of quietly clearing what the server already
   * knows about the venue.
   */
  venuePlaceId: string | null;
  venueAddress: string | null;
  guestCountEstimate: string;
  /** Bare digits — whole dollars. `BudgetEstimateField` owns the commas. */
  budgetEstimate: string;
}

export default function WeddingFormFields<T extends WeddingFormState>({
  value,
  onChange,
  idPrefix,
}: {
  value: T;
  onChange: (next: T) => void;
  /**
   * Both dialogs can be mounted in the same React tree, and an `id` has to be
   * unique across the whole document or `htmlFor` points at whichever input
   * rendered first — which is why the edit dialog's inputs were already
   * prefixed `edit-` before any of this was shared.
   */
  idPrefix: string;
}) {
  const set = (patch: Partial<WeddingFormState>) => onChange({ ...value, ...patch });
  const id = (name: string) => `${idPrefix}-${name}`;

  return (
    <>
      {/* One row per partner, first name then last. Last names are optional
          because plenty of couples share one or have not settled it, and the
          wedding displays by first names either way — the surnames only surface
          when both partners have the same first name. */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={id('partner1First')}>Partner 1 first name *</Label>
          <Input
            id={id('partner1First')}
            autoFocus
            placeholder="Emma"
            value={value.partner1FirstName}
            onChange={(e) => set({ partner1FirstName: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={id('partner1Last')}>Partner 1 last name</Label>
          <Input
            id={id('partner1Last')}
            placeholder="Smith"
            value={value.partner1LastName}
            onChange={(e) => set({ partner1LastName: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={id('partner2First')}>Partner 2 first name *</Label>
          <Input
            id={id('partner2First')}
            placeholder="Liam"
            value={value.partner2FirstName}
            onChange={(e) => set({ partner2FirstName: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={id('partner2Last')}>Partner 2 last name</Label>
          <Input
            id={id('partner2Last')}
            placeholder="Nguyễn"
            value={value.partner2LastName}
            onChange={(e) => set({ partner2LastName: e.target.value })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={id('weddingDate')}>Wedding date</Label>
          <Input
            id={id('weddingDate')}
            type="date"
            value={value.weddingDate}
            onChange={(e) => set({ weddingDate: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={id('venueName')}>Venue</Label>
          <VenueAutocomplete
            id={id('venueName')}
            placeholder="Still deciding"
            value={value.venueName}
            // All three arrive together or not at all — the component never
            // reports a name without also saying what its place id now is.
            onChange={set}
          />
        </div>
      </div>

      {/* Wall-clock at the venue, with no timezone attached — "four to eleven"
          means four to eleven where the wedding is. Sitting directly under the
          date is the point: they are one fact about the day, which is also why
          the two times share a field rather than standing in separate cells. */}
      <WeddingHoursField
        idPrefix={idPrefix}
        startTime={value.startTime}
        endTime={value.endTime}
        onChange={set}
      />

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={id('guestCount')}>Guest estimate</Label>
          <Input
            id={id('guestCount')}
            type="number"
            min={0}
            placeholder="120"
            value={value.guestCountEstimate}
            onChange={(e) => set({ guestCountEstimate: e.target.value })}
          />
        </div>
        <BudgetEstimateField
          id={id('budget')}
          label="Budget estimate"
          value={value.budgetEstimate}
          onChange={(budgetEstimate) => set({ budgetEstimate })}
        />
      </div>
    </>
  );
}
